import type { JSX } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, useLocation } from "react-router-dom";
import { MobileMoreSheet } from "./MobileMoreSheet";

interface TabEntry {
  to: string;
  labelKey: string;
  defaultLabel: string;
  icon: JSX.Element;
  end?: boolean;
}

const ICON_CLS = "w-6 h-6";

const SVG = (path: JSX.Element) => (
  <svg
    className={ICON_CLS}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {path}
  </svg>
);

const TABS: TabEntry[] = [
  {
    to: "/",
    labelKey: "nav.dashboard",
    defaultLabel: "Home",
    icon: SVG(<path d="M3 12l9-9 9 9M5 10v10h14V10" />),
    end: true,
  },
  {
    to: "/networth",
    labelKey: "nav.networth",
    defaultLabel: "Vermögen",
    icon: SVG(<path d="M3 17l6-6 4 4 8-8M14 7h7v7" />),
  },
  {
    to: "/allocation",
    labelKey: "nav.allocation",
    defaultLabel: "Allokation",
    icon: SVG(
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 3v9l6 4" />
      </>,
    ),
  },
  {
    to: "/investments",
    labelKey: "nav.investments",
    defaultLabel: "Invest.",
    icon: SVG(<path d="M4 20V8M10 20V4M16 20v-8M22 20H2" />),
  },
];

const MORE_ICON = SVG(<path d="M5 12h.01M12 12h.01M19 12h.01" />);

export function MobileTabBar() {
  const { t } = useTranslation();
  const location = useLocation();
  const [sheetOpen, setSheetOpen] = useState(false);

  const dailyRoutes = TABS.map((tab) => tab.to);
  const isOnDailyRoute = dailyRoutes.includes(location.pathname) || location.pathname === "/";

  return (
    <>
      <nav
        className="fixed bottom-0 left-0 right-0 z-30 flex border-t md:hidden"
        style={{
          background: "var(--card)",
          borderColor: "var(--border)",
          paddingBottom: "env(safe-area-inset-bottom)",
        }}
        aria-label={t("nav.mobile_tabbar", { defaultValue: "Mobile-Navigation" })}
      >
        {TABS.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={tab.end}
            onClick={() => setSheetOpen(false)}
            className="flex-1 flex flex-col items-center justify-center min-h-[56px] py-1 active:scale-95 transition-transform"
            style={({ isActive }) =>
              isActive ? { color: "var(--accent)" } : { color: "var(--muted)" }
            }
            aria-label={t(tab.labelKey, { defaultValue: tab.defaultLabel })}
          >
            {tab.icon}
            <span className="text-[11px] mt-0.5 font-medium">
              {t(tab.labelKey, { defaultValue: tab.defaultLabel })}
            </span>
          </NavLink>
        ))}
        <button
          type="button"
          onClick={() => setSheetOpen(true)}
          className="flex-1 flex flex-col items-center justify-center min-h-[56px] py-1 active:scale-95 transition-transform"
          style={{
            color: sheetOpen ? "var(--accent)" : isOnDailyRoute ? "var(--muted)" : "var(--accent)",
          }}
          aria-label={t("nav.more", { defaultValue: "Mehr" })}
          aria-expanded={sheetOpen}
        >
          {MORE_ICON}
          <span className="text-[11px] mt-0.5 font-medium">
            {t("nav.more", { defaultValue: "Mehr" })}
          </span>
        </button>
      </nav>
      <MobileMoreSheet open={sheetOpen} onClose={() => setSheetOpen(false)} />
    </>
  );
}
